import { ReactEditor } from 'slate-react'

import { LINE_BREAK_CHARS } from './sanitizePastedText'

// Text input types that slate-react's Android input manager would split on '\n' into soft breaks.
// Composition updates (insertCompositionText, deleteCompositionText) are left to Slate: they fire on
// almost every keystroke while an IME composes, cannot be cancelled, and taking them over breaks typing.
// insertFromPaste normally carries a DataTransfer; it is listed for a browser that sends string data.
const TEXT_INPUT_TYPES = new Set(['insertText', 'insertReplacementText', 'insertFromComposition', 'insertFromYank', 'insertFromPaste'])

/**
 * Insert `beforeinput` text that contains a line break (an Android clipboard paste, IME or autocorrect)
 * through `editor.insertText`, which inserts it as one sanitized line. Returns true when the event was
 * handled; the caller must pass that back to slate-react, because on Android `beforeinput` is usually
 * not cancelable and `preventDefault` alone does not stop Slate from handling it too.
 */
export const handleLineBreakTextInput = (editor: ReactEditor, event: InputEvent): boolean => {
    const { inputType, data } = event
    if (!TEXT_INPUT_TYPES.has(inputType) || typeof data !== 'string' || !LINE_BREAK_CHARS.test(data)) {
        return false
    }

    // Autocorrect replaces a range that may not be the current selection. Inserting at it, rather than
    // selecting it first, leaves the selection alone when the text sanitizes to nothing or is too large.
    const [targetRange] = event.getTargetRanges()
    const at = targetRange ? ReactEditor.toSlateRange(editor, targetRange, { exactMatch: false, suppressThrow: true }) : null

    event.preventDefault()
    editor.insertText(data, at ? { at } : undefined)

    return true
}
